/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState as useLocalState } from "react";
import SubjectLineGenerator from "Components/WritingAssistant/SubjectLineGenerator";
import { writingAssistantStoreData } from "Stores/WritingAssistantData";
import SpintaxParser from "Components/common/SpintaxParser";
import TokenVariable from "Components/common/TokenVariable";

const containsSpintax = (str) => {
  if (typeof str !== "string") return false;
  const regex = /\{((?:[^{}]|\{\{[^{}]*\}\})*\|(?:[^{}]|\{\{[^{}]*\}\})*)\}/;
  return regex.test(str);
};

const containsVariable = (str) => {
  if (typeof str !== "string") return false;
  return /\{\{[^{}]+\}\}/.test(str);
};

const SubjectInput = props => {
  const {
    setSubjectCursorPosition = () => {},
    setSubject = () => {},
    handleFocus = () => {},
    subject = "Registering Value",
    replyAsThread = false,
    replySubject = "Re: (subject of the earlier email in this thread)",
    errorMsg = "",
    setInputValue = () => {},
    inputValue = "",
    content = "",
    id = "subject"
  } = props;

  const subjectText = !replyAsThread ? subject : "";
  const [isFocused, setIsFocused] = React.useState(false);
  const [isEditing, setIsEditing] = useLocalState(false);
  const [showVarPopup, setShowVarPopup] = useLocalState(false);
  const [varTriggerPos, setVarTriggerPos] = useLocalState(0);
  const [popupPosition, setPopupPosition] = useLocalState({ top: 0, left: 0 });
  const inputRef = useRef(null);

  const handleInputFocus = () => {
    writingAssistantStoreData.setShowSubjectLineGenerator(true);
    handleFocus(true);
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    if (!showVarPopup) {
      setIsEditing(false);
    }
  };

  const handleChange = value => {
    const cleaned = value?.replace(/(?:\r\n|\r|\n)/g, "") ?? "";
    setSubject(cleaned, "subject");
    const cursorPos = inputRef.current?.selectionStart ?? cleaned.length;
    if (cleaned.slice(cursorPos - 2, cursorPos) === "{{") {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) {
        setPopupPosition({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
      }
      setVarTriggerPos(cursorPos);
      setShowVarPopup(true);
    }
  };

  const handleVariableSelect = value => {
    const current = subject || "";
    const insertAt = varTriggerPos - 2;
    const newSubject = current.slice(0, insertAt) + value + current.slice(varTriggerPos);
    setSubject(newSubject, "subject");
    setShowVarPopup(false);
    setIsEditing(false);
  };

  useEffect(() => {
    setInputValue({ ...inputValue, subject: subjectText });
  }, [replyAsThread]);

  return (
    <div className={`expandLabelInput subjectInputWithAi ${subject?.length ? "filled" : ""}`}>
      {!replyAsThread && (containsSpintax(subjectText) || containsVariable(subjectText)) && !isEditing ? (
        <div
          className="inputBox spintaxSubjectPreview"
          onClick={() => {
            setIsEditing(true);
            setIsFocused(true);
            writingAssistantStoreData.setShowSubjectLineGenerator(true);
            handleFocus(true);
            setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
          }}
          onBlur={handleInputBlur}
          tabIndex={0}
          style={{
            cursor: "text",
            minHeight: "34px",
            position: "relative",
            zIndex: 100
          }}
        >
          <SpintaxParser
            content={subjectText}
            onChange={(newVal) => setSubject(newVal, "subject")}
            onEditAt={(rawPos) => {
              setIsEditing(true);
              setIsFocused(true);
              writingAssistantStoreData.setShowSubjectLineGenerator(true);
              handleFocus(true);
              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                  inputRef.current.setSelectionRange(rawPos, rawPos);
                }
              }, 0);
            }}
          />
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="text"
            id={id}
            className={`inputBox ${
              replyAsThread ? "disabledInput" : "subjectEnabled"
            }`}
            name="subject"
            value={replyAsThread ? replySubject : subjectText}
            onSelectCapture={e => setSubjectCursorPosition(e.target.selectionStart)}
            onChange={e => handleChange(e.target.value)}
            autoComplete="off"
            required
            readOnly={replyAsThread}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            autoFocus={isFocused}
          />
          {showVarPopup && (
            <div
              className="subjectVariablePopupWrapper"
              style={{ position: "fixed", top: popupPosition.top, left: popupPosition.left, zIndex: 1010 }}
            >
              <TokenVariable
                tokenCbk={handleVariableSelect}
                closeCbk={() => setShowVarPopup(false)}
                pageType="sequences"
                showInboxTokens={true}
                disableInboxTokens={true}
                showDateTokens={true}
              />
            </div>
          )}
        </>
      )}
      {!replyAsThread && <span className="focus-border"></span>}
      <label
        htmlFor="subject"
        className={`inputLabel ${replyAsThread ? "disabledInputLabel" : ""}`}
      >
        Subject<sup>*</sup>
      </label>
      {errorMsg && <span className="errMsgText">{errorMsg}</span>}
      {!replyAsThread && (
        <SubjectLineGenerator
          type="sequences"
          content={content}
          replaceSubjectCbk={text => setSubject(text, "subject")}
        />
      )}
    </div>
  );
};

export { SubjectInput };
export default SubjectInput;
